import React, { useState, useEffect, useCallback } from 'react';
import {
  Plug,
  BookOpen,
  Mail,
  ShieldCheck,
  RotateCcw,
  ExternalLink,
  Copy,
  Check,
  Activity,
  Zap,
  Search,
  FileCode,
  Terminal,
  AlertTriangle,
  Compass,
  Network,
} from 'lucide-react';
import { apiClient } from '../../api/client';
import { ProjectGroundingView } from '../grounding/ProjectGroundingView';
import { SystemCapabilityMapView } from '../capabilities/SystemCapabilityMapView';
import { SYSTEM_BILINGUAL } from '../../shared/constants/bilingualTitles';

export interface TelemetryFreshness {
  last_ingestion_utc?: string;
  last_derived_snapshot_utc?: string;
  last_price_probe_utc?: string;
  last_completed_cycle_utc?: string;
  event_loop_lag_ms?: number;
  worker_backlog_count?: number;
  coalesced_cycle_count?: number;
}

export interface MarketStatusData {
  service: string;
  is_running: boolean;
  uptime_seconds: number;
  total_ingested_count: number;
  cached_candle_count: number;
  error_count: number;
  last_error: string | null;
  last_cycle_utc: string;
  p95_latency_ms: number;
  active_providers?: Record<string, string>;
  database?: {
    backend: string;
    healthy: boolean;
    pool_active?: number;
    pool_available?: number;
    replication_lag_seconds?: string;
    outbox_pending_count?: number;
  };
  telemetry_freshness?: TelemetryFreshness;
}

export const IntegrationsAuditHub: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'onboarding' | 'mailbox' | 'audit' | 'grounding' | 'capabilities'>('onboarding');
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [codeLang, setCodeLang] = useState<'python' | 'ts' | 'curl'>('python');
  const [contractDomainFilter, setContractDomainFilter] = useState<'all' | 'crypto' | 'ashare' | 'macro' | 'system'>('all');

  // Deep-link query parameter synchronization (#/gateway?tab=mailbox, #/gateway?tab=grounding, etc.)
  useEffect(() => {
    const handleUrlTab = () => {
      const hash = window.location.hash;
      if (hash.includes('tab=capabilities') || hash.includes('/capabilities')) {
        setActiveTab('capabilities');
      } else if (hash.includes('tab=mailbox')) {
        setActiveTab('mailbox');
      } else if (hash.includes('tab=audit')) {
        setActiveTab('audit');
      } else if (hash.includes('tab=grounding') || hash.includes('/grounding') || hash.includes('#grounding')) {
        setActiveTab('grounding');
      } else if (hash.includes('tab=onboarding')) {
        setActiveTab('onboarding');
      }
    };
    handleUrlTab();
    window.addEventListener('hashchange', handleUrlTab);
    return () => window.removeEventListener('hashchange', handleUrlTab);
  }, []);

  const handleTabChange = (tab: 'onboarding' | 'mailbox' | 'audit' | 'grounding' | 'capabilities') => {
    setActiveTab(tab);
    window.location.hash = `#/system/gateway?tab=${tab}`;
  };

  // Mailbox interactive state
  const [ticketQueryId, setTicketQueryId] = useState<string>('');
  const [ticketResult, setTicketResult] = useState<any | null>(null);
  const [ticketLoading, setTicketLoading] = useState<boolean>(false);
  const [ticketError, setTicketError] = useState<string | null>(null);
  const [capabilitiesData, setCapabilitiesData] = useState<any | null>(null);
  const [capLoading, setCapLoading] = useState<boolean>(false);

  // Real-time status data for the audit pillar
  const [statusData, setStatusData] = useState<MarketStatusData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [lastRefreshed, setLastRefreshed] = useState<string>('--:--:--');

  const fetchStatus = useCallback(async () => {
    try {
      const data = await apiClient.get<MarketStatusData>('/api/v1/market/status');
      setStatusData(data);
      setErrorMsg(null);
      setLastRefreshed(new Date().toLocaleTimeString('zh-CN', { hour12: false }));
    } catch (err: any) {
      console.error('Failed to fetch status for IntegrationsAuditHub:', err);
      setErrorMsg(err?.message || '无法连接到 Market-Hub 实时状态接口');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const timer = setInterval(fetchStatus, 5000);
    return () => clearInterval(timer);
  }, [fetchStatus]);

  const handleCopy = (key: string, text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const fetchCapabilities = useCallback(async () => {
    try {
      setCapLoading(true);
      const data = await apiClient.get<any>('/api/v1/meta/capabilities');
      setCapabilitiesData(data);
    } catch (err: any) {
      console.warn('Failed to fetch capabilities:', err);
    } finally {
      setCapLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'mailbox' && !capabilitiesData) {
      fetchCapabilities();
    }
  }, [activeTab, capabilitiesData, fetchCapabilities]);

  const handleLookupTicket = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!ticketQueryId.trim()) return;
    setTicketLoading(true);
    setTicketError(null);
    setTicketResult(null);
    try {
      const data = await apiClient.get<any>(`/api/v1/meta/mailbox/status/${encodeURIComponent(ticketQueryId.trim().toUpperCase())}`);
      setTicketResult(data);
    } catch (err: any) {
      setTicketError(err?.message || '未找到该需求工单或网络异常');
    } finally {
      setTicketLoading(false);
    }
  };

  // 1-Click Cross-Project Agent Prompts (Stage 085 / ASOP v1.9.6)
  const doctoraAgentPrompt = `Role: Doctor-A A-Share Quant Risk & Factor Agent
Task: Connect to Market-Hub Forward Capability Mesh & Asynchronous Mailbox.
Governance: ASOP v1.9.6 / Stage 085

1. Capabilities Discovery:
   - Dynamic SSOT: GET https://market-hub.20991234.xyz:8443/api/v1/meta/capabilities
   - Handbook: GET https://market-hub.20991234.xyz:8443/api/v1/meta/agent-handbook
   - Machine Context: GET https://market-hub.20991234.xyz:8443/api/v1/meta/llms.txt

2. Authentication & Replay Defense (Direct TLS :8443):
   - Transport: Direct backend TLS port :8443 (Port 443 is browser-only BFF; rejects external token requests with 403 Forbidden).
   - Authorization: Bearer $DOCTORA_AUTH_TOKEN
   - Scopes: read:ashare:*, write:ashare:watchlist
   - X-TSO-Tenant-ID: TENANT_DOCTORA_EQUITY
   - X-TSO-Timestamp-UTC: <ISO_8601_UTC_Z> (drift <= 5.0s)
   - X-TSO-Nonce: <UUIDv4>

3. Trading Calendar & Watchlist Sync:
   - Calendar: Asia/Shanghai dual-session (09:30-11:30, 13:00-15:00 CST; weekend/holiday CLOSED).
   - Symbol Format: Canonical .SH, .SZ, .BJ suffix (e.g. 600519.SH, 000001.SZ).
   - Watchlist Cap: Max 300 active symbols per sync payload.
   - Endpoint: POST https://market-hub.20991234.xyz:8443/api/v1/market/a-share/watchlist/sync

4. Error Semantics & Invariants:
   - 401: Invalid or missing token; 403: Forbidden or cross-tenant mismatch.
   - 429: Rate limit exceeded (back off per Retry-After header).
   - 503: MARKET_SOURCE_EXHAUSTED; fail closed immediately (zero synthetic mock fallbacks).
   - 100% Pure Decimal on all price, spread, and factor outputs.`;

  const tsoAgentPrompt = `Role: TSO Crypto Strategy & Indicator Agent
Task: Connect to Market-Hub Forward Capability Mesh & Asynchronous Mailbox.
Governance: ASOP v1.9.6 / Stage 085

1. Capabilities Discovery:
   - Dynamic SSOT: GET https://market-hub.20991234.xyz:8443/api/v1/meta/capabilities
   - Handbook: GET https://market-hub.20991234.xyz:8443/api/v1/meta/agent-handbook
   - Machine Context: GET https://market-hub.20991234.xyz:8443/api/v1/meta/llms.txt

2. Authentication & Replay Defense (Direct TLS :8443):
   - Transport: Direct backend TLS port :8443 (Port 443 is browser-only BFF).
   - Authorization: Bearer $MARKET_HUB_AUTH_TOKEN
   - Scopes: read:crypto:*, read:indicators
   - X-TSO-Tenant-ID: TENANT_TSO_CRYPTO
   - X-TSO-Timestamp-UTC: <ISO_8601_UTC_Z> (drift <= 5.0s)
   - X-TSO-Nonce: <UUIDv4>

3. Multi-Cadence Polling & Causal Lag Invariant:
   - Polling Cadences: 1m (10s), 5m (30s), 15m (60s).
   - Causal Lag: Enforce W >= 3s (close_time_utc <= now_utc - 3s).
   - 5-State Machine: loading | fresh | stale | disconnected | empty.
   - 100% Pure Decimal arithmetic; zero float conversions.

4. Error Semantics:
   - Handle 401 Unauthorized, 403 Forbidden, 429 Rate Limit (Retry-After), 503 Fail-Closed.`;

  const markethubReviewPrompt = `Role: Market-Hub Architecture Lead / Reviewer
Task: Inspect and evaluate pending downstream quant demands in Ecosystem Mailbox.
Governance: ASOP v1.9.6 / Stage 085

1. Administrative Inbox Review:
   - Query: GET https://market-hub.20991234.xyz:8443/api/v1/meta/mailbox/inbox
   - Auth: Bearer $MARKET_HUB_ADMIN_TOKEN (TENANT_INTERNAL_OBSERVER)

2. Feasibility & Architecture Check:
   - Check against Pure Decimal financial domain, causal lag (W >= 3.0s), and read-only physical lock.
   - Strictly reject any trading/execution/broker routing demands.

3. Formal Reply Dispatch:
   - POST https://market-hub.20991234.xyz:8443/api/v1/meta/mailbox/reply
   - Payload:
     {
       "ticket_id": "<TICKET_ID>",
       "reply": {
         "decision": "ACCEPTED" | "UNDER_EVALUATION" | "REJECTED",
         "scheduled_stage": "STAGE_086",
         "reply_note": "<ARCHITECTURAL_RATIONALE>",
         "target_endpoint": "<ENDPOINT>"
       }
     }`;

  // Autonomous Agent One-Step Integration Prompt
  const agentPrompt = `Connect our quant service to Market-Hub as an authoritative market data client:
1. Physical Endpoint: Target Direct API TLS port 'https://market-hub.20991234.xyz:8443' (DO NOT query port 443; port 443 is browser-only BFF and blocks external token requests with HTTP 403).
2. Mandatory Headers: Include 'Authorization: Bearer <TENANT_TOKEN>', 'X-TSO-Tenant-ID' (TENANT_TSO_CRYPTO or TENANT_DOCTORA_EQUITY), 'X-TSO-Timestamp-UTC' (ISO-8601 UTC with Z, max drift +-5.0s), and 'X-TSO-Nonce' (UUIDv4 per request, 5.0s deduplication window).
3. Downstream Starter Scopes & Safeguards:
   - TSO Crypto: Consume /api/v2/trend-weather/matrix with multi-cadence polling (1m/10s, 5m/30s, 15m/60s), W >= 3s causal lag check, and 5-state machine handling.
   - Doctor-A A-Share: Access /api/v1/market/a-share/screener-pool and sync /watchlist/sync (requires 'write:ashare:watchlist' scope, 300 active symbol cap, canonical .SH/.SZ/.BJ symbols, Asia/Shanghai trading calendar).
4. Precision & Error Invariants:
   - Financial-grade 100% pure Decimal for all prices, spreads, and yields (e.g. Python json.loads(text, parse_float=Decimal)). Zero binary floats.
   - Handle HTTP 401 (Unauthorized) and HTTP 403 (Forbidden). Respect 100 QPS rate limits (back off on HTTP 429 per Retry-After). On HTTP 503 fail closed immediately (MARKET_SOURCE_EXHAUSTED; zero synthetic mock fallbacks).
Implement the client module, load credentials from environment variables, and verify connection via GET /api/v1/market/basis?scope=core7.`;

  // Code Snippets
  const pythonSnippet = `import asyncio
import json
import os
import uuid
from datetime import datetime, timezone, timedelta
from decimal import Decimal
from typing import Any, Dict, List, Optional, Union
import httpx

# 物理网络接入端点 SSOT:
# 1. 公网直连 (Direct API): https://market-hub.20991234.xyz:8443 (必须带 Authorization)
# 2. 内网容器直连 (Intranet): http://127.0.0.1:8000
# 注意: 端口 443 是前端 Web SPA Ingress-BFF，外部携带 Token 请求将被 403 阻断！
MARKET_HUB_BASE_URL = os.getenv("MARKET_HUB_BASE_URL", "https://market-hub.20991234.xyz:8443")

# 租户凭证与租户身份 (独立凭证隔离)
# TSO 加密系统使用: TENANT_TSO_CRYPTO
# Doctor-A 多资产风控使用: TENANT_DOCTORA_EQUITY (需包含 write:ashare:watchlist 权限)
TSO_TOKEN = os.getenv("MARKET_HUB_TSO_TOKEN", "your-tso-token-here")
TENANT_TSO_ID = "TENANT_TSO_CRYPTO"

DOCTORA_TOKEN = os.getenv("MARKET_HUB_DOCTORA_TOKEN", "your-doctora-token-here")
TENANT_DOCTORA_ID = "TENANT_DOCTORA_EQUITY"

# ==========================================
# 1. TSO 专用接入套件 (多周期气象与基差时钟)
# ==========================================
async def fetch_tso_weather_and_basis():
    """
    TSO 策略引擎接入:
    - 轮询节奏: 1m(10s), 5m(30s), 15m(60s)
    - 因果时延校验: W >= 3s (close_time <= now_utc - 3s)
    - 5态状态机: loading | fresh | stale | disconnected | empty
    - 100% 纯 Decimal 精度保证
    """
    async with httpx.AsyncClient(timeout=5) as client:
        # 获取 7x7 趋势气象 (带 429 指数退避与全新 Nonce)
        last_resp = None
        for attempt in range(3):
            now_utc = datetime.now(timezone.utc).strftime("%Y-%m-%dT%H:%M:%SZ")
            headers = {
                "Authorization": f"Bearer {TSO_TOKEN}",
                "X-TSO-Tenant-ID": TENANT_TSO_ID,
                "X-TSO-Timestamp-UTC": now_utc,
                "X-TSO-Nonce": str(uuid.uuid4()),
                "Accept": "application/json",
            }
            resp = await client.get(f"{MARKET_HUB_BASE_URL}/api/v2/trend-weather/matrix", headers=headers)
            last_resp = resp
            if resp.status_code == 429:
                retry_after = int(resp.headers.get("Retry-After", "1"))
                await asyncio.sleep(min(retry_after, 5))
                continue
            if resp.status_code in (401, 403):
                raise PermissionError(f"Authentication failed: HTTP {resp.status_code}")
            if resp.status_code == 503:
                raise RuntimeError("MARKET_SOURCE_EXHAUSTED: Fail-closed terminal state")
            resp.raise_for_status()
            break
        else:
            raise RuntimeError(f"TSO weather request failed after 3 attempts: HTTP {getattr(last_resp, 'status_code', 'unknown')}")

        weather = json.loads(resp.text, parse_float=Decimal)

        # 5态状态机校验: 阻断非正常运行态数据泄露 (仅接受 FRESH 运行态，拒绝 LOADING/UNKNOWN/STALE 等)
        status_raw = str(weather.get("status", "")).upper()
        if status_raw != "FRESH" or weather.get("stale") is True:
            raise RuntimeError(f"TSO weather feed in non-operational state: {status_raw}")

        # 因果时延守卫: W >= 3s，严防未来时间戳与未闭合 K 线 (提取 bar_close_utc)
        close_time_str = weather.get("bar_close_utc") or weather.get("close_time_utc")
        if not close_time_str:
            raise ValueError("Missing bar_close_utc in weather payload")

        close_dt = datetime.fromisoformat(close_time_str.replace("Z", "+00:00"))
        now_dt = datetime.now(timezone.utc)
        if close_dt > now_dt - timedelta(seconds=3):
            raise ValueError(f"Causal lag violation: bar_close_utc {close_time_str} is within 3s window or in future")

        # 逐 Cell 单元格级别因果时延与状态校验 (时间戳必须存在且满足因果时延)
        symbols_data = weather.get("symbols", [])
        if isinstance(symbols_data, list):
            for s in symbols_data:
                cells = s.get("cells", {})
                for tf, cell in cells.items():
                    if cell.get("stale") is True:
                        raise RuntimeError(f"TSO weather cell {s.get('symbol')}:{tf} is stale")
                    cell_stamp = cell.get("bar_close_utc") or cell.get("close_time_utc")
                    if not cell_stamp:
                        raise ValueError(f"Missing bar_close_utc in cell {s.get('symbol')}:{tf}")
                    cell_dt = datetime.fromisoformat(cell_stamp.replace("Z", "+00:00"))
                    if cell_dt > now_dt - timedelta(seconds=3):
                        raise ValueError(f"Causal lag violation in cell {s.get('symbol')}:{tf}: {cell_stamp}")

        return weather

# ==========================================
# 2. Doctor-A 专用接入套件 (战术自选池同步)
# ==========================================
async def sync_doctora_watchlist(symbols: Union[List[str], List[Dict[str, Any]]], action: str = "ADD"):
    """
    Doctor-A 战术自选池同步套件:
    - 权限要求: write:ashare:watchlist
    - 交易日历: Asia/Shanghai 双 Session (09:30-11:30, 13:00-15:00)
    - 标的规范: 规范化 .SH / .SZ / .BJ 后缀与标准结构体
    - 容量上限: 300 标的硬上限 (超出将触发 HTTP 400 校验拒绝)
    - 默认操作: ADD 增量同步 (显式指定 REPLACE 方可执行覆盖)
    """
    if len(symbols) > 300:
        raise ValueError("Watchlist symbol count exceeds hard limit of 300")

    # 规范化标的结构为 List[Dict[str, Any]]，完全对齐后端 WatchlistSyncPayload
    norm_symbols = [{"symbol": s} if isinstance(s, str) else s for s in symbols]

    now_utc = datetime.now(timezone.utc).strftime("%Y-%m-%dT%H:%M:%SZ")
    headers = {
        "Authorization": f"Bearer {DOCTORA_TOKEN}",
        "X-TSO-Tenant-ID": TENANT_DOCTORA_ID,
        "X-TSO-Timestamp-UTC": now_utc,
        "X-TSO-Nonce": str(uuid.uuid4()),
        "Content-Type": "application/json",
    }
    payload = {
        "action": action,
        "source": "DOCTOR_A",
        "symbols": norm_symbols,
    }
    async with httpx.AsyncClient(timeout=8) as client:
        resp = await client.post(
            f"{MARKET_HUB_BASE_URL}/api/v1/market/a-share/watchlist/sync",
            headers=headers,
            json=payload,
        )
        if resp.status_code in (401, 403):
            raise PermissionError(f"Permission denied (requires write:ashare:watchlist): HTTP {resp.status_code}")
        if resp.status_code != 200:
            raise RuntimeError(f"Watchlist sync failed with HTTP {resp.status_code}: {resp.text}")
        return resp.json()

if __name__ == "__main__":
    result = asyncio.run(fetch_tso_weather_and_basis())
    print("TSO Trend Weather symbols:", len(result.get("symbols", [])))
`;

  const tsSnippet = `import { v4 as uuidv4 } from 'uuid';

// 物理网络端点: 外部直连请固定访问 TLS :8443，内网访问使用 http://127.0.0.1:8000
const MARKET_HUB_BASE = process.env.MARKET_HUB_BASE_URL || 'https://market-hub.20991234.xyz:8443';
const TSO_TOKEN = process.env.MARKET_HUB_TSO_TOKEN || 'your-tso-token-here';
const DOCTORA_TOKEN = process.env.MARKET_HUB_DOCTORA_TOKEN || 'your-doctora-token-here';

// 1. TSO 加密行情与指标套件 (因果时延 W >= 3s & 5 态状态机守卫)
export async function fetchTsoWeather() {
  const headers: Record<string, string> = {
    'Authorization': \`Bearer \${TSO_TOKEN}\`,
    'X-TSO-Tenant-ID': 'TENANT_TSO_CRYPTO',
    'X-TSO-Timestamp-UTC': new Date().toISOString().replace(/\\.\\d{3}Z$/, 'Z'),
    'X-TSO-Nonce': uuidv4(),
    'Accept': 'application/json',
  };
  const resp = await fetch(\`\${MARKET_HUB_BASE}/api/v2/trend-weather/matrix\`, { headers });
  if (resp.status === 401 || resp.status === 403) {
    throw new Error(\`Authentication failed with HTTP \${resp.status}\`);
  }
  if (resp.status === 503) {
    throw new Error('MARKET_SOURCE_EXHAUSTED: Service in fail-closed state');
  }
  if (!resp.ok) {
    throw new Error(\`TSO weather request failed with HTTP \${resp.status}: \${await resp.text()}\`);
  }
  const data = await resp.json();
  const statusUpper = String(data.status || '').toUpperCase();
  if (statusUpper !== 'FRESH' || data.stale === true) {
    throw new Error(\`TSO weather feed in non-operational state: \${statusUpper}\`);
  }
  const barClose = data.bar_close_utc || data.close_time_utc;
  if (!barClose) {
    throw new Error('Missing bar_close_utc in weather payload');
  }
  const closeMs = new Date(barClose).getTime();
  const nowMs = Date.now();
  if (!Number.isFinite(closeMs) || closeMs > nowMs - 3000) {
    throw new Error(\`Causal lag violation or invalid timestamp: bar_close_utc \${barClose}\`);
  }
  if (Array.isArray(data.symbols)) {
    for (const s of data.symbols) {
      for (const [tf, cell] of Object.entries(s.cells || {})) {
        if ((cell as any).stale === true) {
          throw new Error(\`TSO weather cell \${s.symbol}:\${tf} is stale\`);
        }
        const cellClose = (cell as any).bar_close_utc || (cell as any).close_time_utc;
        if (!cellClose) {
          throw new Error(\`Missing bar_close_utc in cell \${s.symbol}:\${tf}\`);
        }
        const cellCloseMs = new Date(cellClose).getTime();
        if (!Number.isFinite(cellCloseMs) || cellCloseMs > nowMs - 3000) {
          throw new Error(\`Causal lag violation or invalid timestamp in cell \${s.symbol}:\${tf}: \${cellClose}\`);
        }
      }
    }
  }
  return data;
}

// 2. Doctor-A 战术自选池同步套件 (需 write:ashare:watchlist，上限 300 标的，Asia/Shanghai 交易日历)
export async function syncDoctorAWatchlist(
  symbols: Array<string | { symbol: string; [key: string]: any }>,
  action: 'ADD' | 'REMOVE' | 'REPLACE' = 'ADD'
) {
  if (symbols.length > 300) {
    throw new Error('Active watchlist cap exceeded (max 300 symbols allowed)');
  }
  const normSymbols = symbols.map(s => typeof s === 'string' ? { symbol: s } : s);
  const headers: Record<string, string> = {
    'Authorization': \`Bearer \${DOCTORA_TOKEN}\`,
    'X-TSO-Tenant-ID': 'TENANT_DOCTORA_EQUITY',
    'X-TSO-Timestamp-UTC': new Date().toISOString().replace(/\\.\\d{3}Z$/, 'Z'),
    'X-TSO-Nonce': uuidv4(),
    'Content-Type': 'application/json',
  };
  const resp = await fetch(\`\${MARKET_HUB_BASE}/api/v1/market/a-share/watchlist/sync\`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ action, source: 'DOCTOR_A', symbols: normSymbols }),
  });
  if (resp.status === 401 || resp.status === 403) {
    throw new Error(\`Watchlist sync forbidden (check write:ashare:watchlist scope): HTTP \${resp.status}\`);
  }
  if (!resp.ok) {
    throw new Error(\`Watchlist sync failed with HTTP \${resp.status}: \${await resp.text()}\`);
  }
  return await resp.json();
}
`;

  const curlSnippet = `# 1. 基础健康检查探测 (直连后端 :8443，必须带 Authorization)
curl -s -H "Authorization: Bearer YOUR_TSO_TOKEN" \\
     -w "\\nTime: %{time_total}s\\n" \\
     https://market-hub.20991234.xyz:8443/health

# 2. TSO 加密基差时钟 (外部 direct 调用必须走 :8443 端口并携带全套 X-TSO-* Headers)
curl -s -H "Authorization: Bearer YOUR_TSO_TOKEN" \\
     -H "X-TSO-Tenant-ID: TENANT_TSO_CRYPTO" \\
     -H "X-TSO-Timestamp-UTC: $(date -u +%Y-%m-%dT%H:%M:%SZ)" \\
     -H "X-TSO-Nonce: $(uuidgen 2>/dev/null || cat /proc/sys/kernel/random/uuid)" \\
     "https://market-hub.20991234.xyz:8443/api/v1/market/basis?scope=core7"

# 3. Doctor-A 专属: 战术自选池同步 (需 write:ashare:watchlist 权限，上限 300 标的，Asia/Shanghai 交易日历)
curl -s -X POST -H "Authorization: Bearer YOUR_DOCTORA_TOKEN" \\
     -H "X-TSO-Tenant-ID: TENANT_DOCTORA_EQUITY" \\
     -H "X-TSO-Timestamp-UTC: $(date -u +%Y-%m-%dT%H:%M:%SZ)" \\
     -H "X-TSO-Nonce: $(uuidgen 2>/dev/null || cat /proc/sys/kernel/random/uuid)" \\
     -H "Content-Type: application/json" \\
     -d '{"action": "ADD", "source": "DOCTOR_A", "symbols": [{"symbol": "600519.SH"}, {"symbol": "000001.SZ"}]}' \\
     "https://market-hub.20991234.xyz:8443/api/v1/market/a-share/watchlist/sync"

# 4. 全球宏观核心锚点 (原油/黄金/美债) 纯 Decimal 接口
curl -s -H "Authorization: Bearer YOUR_DOCTORA_TOKEN" \\
     -H "X-TSO-Tenant-ID: TENANT_DOCTORA_EQUITY" \\
     -H "X-TSO-Timestamp-UTC: $(date -u +%Y-%m-%dT%H:%M:%SZ)" \\
     -H "X-TSO-Nonce: $(uuidgen 2>/dev/null || cat /proc/sys/kernel/random/uuid)" \\
     "https://market-hub.20991234.xyz:8443/api/v1/market/macro/anchors"

# 5. 错误与鉴权语义说明:
# - HTTP 400: 容量超限 (>300 标的) 或携带非法交易字段 (order_id/budget/pnl)
# - HTTP 401: 凭证缺失或无效
# - HTTP 403: 权限不足 (如未授予 write:ashare:watchlist) 或跨租户伪造
# - HTTP 429: 触发限流，需读取 Retry-After 进行指数退避
# - HTTP 503: MARKET_SOURCE_EXHAUSTED 真实性熔断
`;

  const contracts = [
    // 1. Crypto Quantitative Domain
    {
      id: 'basis-core7',
      domain: 'crypto',
      name: 'Core-7 现货/永续实时基差时钟',
      endpoint: '/api/v1/market/basis?scope=core7',
      method: 'GET',
      sla: '< 5 ms',
      consumer: 'TSO 资金费率对冲 / 基差套利引擎',
      fields: ['symbol', 'spot_price', 'perp_price', 'basis_spread_usd', 'basis_spread_bps', 'annualized_rate_pct', 'as_of_utc'],
      schemaDesc: '包含 BTC, ETH, SOL, BNB, OKB, BGB, HYPE 7 大标的的高精度基差差值与年化率，100% 纯 Decimal',
    },
    {
      id: 'regime-weather',
      domain: 'crypto',
      name: '7x7 趋势气象与齿轮传动矩阵',
      endpoint: '/api/v2/trend-weather/matrix',
      method: 'GET',
      sla: '< 10 ms',
      consumer: 'TSO 宏观气候 / Doctor-A 组合风控',
      fields: ['symbols', 'timeframes', 'weather_matrix', 'gear_transmission', 'forecast', 'scenarios', 'as_of_utc'],
      schemaDesc: '7 币种跨 7 周期 (1m~1d) 量化事实标签、30/30/40 对称情景概率与大周期齿轮传动约束',
    },
    {
      id: 'crypto-indicators',
      domain: 'crypto',
      name: '多周期技术指标与特征矩阵',
      endpoint: '/api/v1/indicators/matrix',
      method: 'GET',
      sla: '< 10 ms',
      consumer: 'TSO 信号发生器 / 策略回测器',
      fields: ['symbol', 'timeframe', 'ema20', 'ema50', 'rsi14', 'atr14', 'macd', 'bollinger', 'as_of_utc'],
      schemaDesc: '纯 Decimal 结构化指标时序，零浮点漂移',
    },
    {
      id: 'crypto-strategist-brief',
      domain: 'crypto',
      name: 'AI 首席策略师实盘研判内参',
      endpoint: '/api/v1/market/crypto/strategist/brief',
      method: 'GET',
      sla: '< 15 ms',
      consumer: 'TSO 宏观风控守门 / 自动化决策',
      fields: ['status', 'symbols', 'window_start_utc', 'window_end_utc', 'primary_scenario', 'narrative', 'generated_at_utc'],
      schemaDesc: '全市场核心多资产结构化研判快照，严格遵循 StrategistReportContractV1 响应模型',
    },
    {
      id: 'crypto-strategy-matrix',
      domain: 'crypto',
      name: '跨周期策略共振与协同矩阵',
      endpoint: '/api/v1/regime/strategy-matrix',
      method: 'GET',
      sla: '< 12 ms',
      consumer: 'TSO 策略共振引擎',
      fields: ['symbol', 'timeframe', 'freshness_status', 'metrics', 'generated_at_utc', 'as_of_utc'],
      schemaDesc: '单周期量化策略特征评估与动态信号输出，遵循 StrategyMatrixResponse 模型，联动 /cross-tf 扩展',
    },
    {
      id: 'crypto-strategy-matrix-cross-tf',
      domain: 'crypto',
      name: '跨周期策略共振与协同全景',
      endpoint: '/api/v1/regime/strategy-matrix/cross-tf',
      method: 'GET',
      sla: '< 15 ms',
      consumer: 'TSO 多周期决策中枢',
      fields: ['symbol', 'timeframe', 'freshness_status', 'metrics', 'generated_at_utc', 'as_of_utc'],
      schemaDesc: '跨周期策略共振协同快照，遵循 StrategyMatrixResponse 模型 (timeframe=CROSS_TF)',
    },
    {
      id: 'crypto-paper-trading-positions',
      domain: 'crypto',
      name: '实盘模拟仿真持仓与净值曲线',
      endpoint: '/api/v1/backtest/paper-trading/positions',
      method: 'GET',
      sla: '< 15 ms',
      consumer: 'TSO 模拟执行观察台 / 策略验真',
      fields: ['status', 'symbol', 'count', 'positions'],
      schemaDesc: '只读观测性模拟持仓快照，联动 /summary, /trades, /equity-curve 镜像端点',
    },

    // 2. A-Share Investment & Risk Domain
    {
      id: 'ashare-screener-pool',
      domain: 'ashare',
      name: 'A 股多因子选股全市场初筛池',
      endpoint: '/api/v1/market/a-share/screener-pool',
      method: 'GET',
      sla: '< 20 ms',
      consumer: 'Doctor-A 选股中枢 / 因子过滤',
      fields: ['symbol', 'name', 'market_cap', 'pe_ttm', 'turnover_ratio', 'momentum_score', 'sector'],
      schemaDesc: '全市场 5,000+ 标的多因子初筛，支持流动性与估值约束',
    },
    {
      id: 'ashare-radar-pool',
      domain: 'ashare',
      name: 'A 股潜伏伏击量化雷达候选池 (5,563 标的)',
      endpoint: '/api/v1/market/a-share/radar/pool',
      method: 'GET',
      sla: '< 20 ms',
      consumer: 'Doctor-A 潜伏雷达 / 跨市场阿尔法追踪',
      fields: ['symbol', 'stock_name', 'composite_score', 'trend_score', 'surge_score', 'quant_signals', 'sector'],
      schemaDesc: '全市场 5,563 只 A 股日终回填与纯 Decimal 量化特征评分，锚定沪深 300 基准',
    },
    {
      id: 'ashare-ct-matrix',
      domain: 'ashare',
      name: 'A 股多维 CT 扫描技术矩阵',
      endpoint: '/api/v1/market/a-share/ct-matrix',
      method: 'GET',
      sla: '< 20 ms',
      consumer: 'Doctor-A 技术扫描 / 状态识别',
      fields: ['symbol', 'scan_results', 'as_of_utc', 'status'],
      schemaDesc: '多周期技术形态多维穿透扫描，支持实时与日终模式',
    },
    {
      id: 'ashare-stock-tags',
      domain: 'ashare',
      name: 'A 股个股量化事实与形态特征标签',
      endpoint: '/api/v1/market/a-share/stock/{symbol}/tags',
      method: 'GET',
      sla: '< 15 ms',
      consumer: 'Doctor-A 因子穿透 / 批量标签归因',
      fields: ['symbol', 'tags', 'as_of_utc', 'status'],
      schemaDesc: '单标的纯 Decimal 特征标签集，支持 /tags/batch 批量查询',
    },
    {
      id: 'ashare-sector-dictionary',
      domain: 'ashare',
      name: '申万行业分类与板块字典',
      endpoint: '/api/v1/market/a-share/sector/dictionary',
      method: 'GET',
      sla: '< 10 ms',
      consumer: 'Doctor-A 行业轮动 / 风险暴露归因',
      fields: ['sector_id', 'sector_name', 'category', 'hierarchy_level', 'constituents_count'],
      schemaDesc: '权威申万行业与概念分类元数据字典，支持 sector_id / category 过滤',
    },
    {
      id: 'ashare-main-profile',
      domain: 'ashare',
      name: 'A 股主力筹码集中度画像',
      endpoint: '/api/v1/market/a-share/main-profile/{symbol}',
      method: 'GET',
      sla: '< 15 ms',
      consumer: 'Doctor-A 筹码结构研判 / 主力吸筹穿透',
      fields: ['symbol', 'concentration_ratio', 'top10_holder_pct', 'institutional_flow_90d', 'chip_dispersion'],
      schemaDesc: '90 天主力资金与机构筹码密集区穿透分布，支持 /api/v1/market/a-share/main-profile/screener',
    },
    {
      id: 'ashare-watchlist-items',
      domain: 'ashare',
      name: 'Doctor-A 战术自选池标的与指标',
      endpoint: '/api/v1/market/a-share/watchlist/items',
      method: 'GET',
      sla: '< 10 ms',
      consumer: 'Doctor-A 实时行情监控 / 盘中预警',
      fields: ['symbol', 'name', 'current_price', 'change_pct', 'volume', 'tags', 'added_at_utc'],
      schemaDesc: '自选池标的实时行情与 1m 蜡烛线时序 (/api/v1/market/a-share/watchlist/candles)',
    },
    {
      id: 'ashare-watchlist-sync',
      domain: 'ashare',
      name: 'Doctor-A 战术自选池受控同步',
      endpoint: '/api/v1/market/a-share/watchlist/sync',
      method: 'POST',
      sla: '< 30 ms',
      consumer: 'Doctor-A 战术自选标的动态调整',
      fields: ['action', 'source', 'symbols', 'sync_result', 'active_count', 'updated_at_utc'],
      schemaDesc: '需 write:ashare:watchlist 授权，上限 300 标的，支持 ADD/REMOVE/REPLACE',
    },

    // 3. Macro Corridor Domain
    {
      id: 'macro-anchors',
      domain: 'macro',
      name: '全球宏观核心锚点 (原油/黄金/美债)',
      endpoint: '/api/v1/market/macro/anchors',
      method: 'GET',
      sla: '< 10 ms',
      consumer: 'Doctor-A 宏观风控 / TSO 宏观罗盘',
      fields: ['gold', 'crude_oil', 'dxy', 'us10y', 'regime_pulse', 'updated_at_utc'],
      schemaDesc: '新浪/雅虎双源每 2 分钟调度的宏观态势锚点，100% 纯 Decimal 数据流',
    },
    {
      id: 'macro-quotes',
      domain: 'macro',
      name: '全球大宗商品与主要外汇实时行情',
      endpoint: '/api/v1/market/macro/quotes',
      method: 'GET',
      sla: '< 10 ms',
      consumer: '宏观对冲策略 / 跨资产估值模型',
      fields: ['quotes', 'source', 'quote_count', 'freshness_state', 'as_of_utc'],
      schemaDesc: '全球核心大宗商品、外汇与利差纯 Decimal 报价聚合',
    },
    {
      id: 'macro-pulse',
      domain: 'macro',
      name: '宏观情绪与跨资产脉搏雷达',
      endpoint: '/api/v1/market/macro/pulse',
      method: 'GET',
      sla: '< 15 ms',
      consumer: '跨资产风控中枢',
      fields: ['risk_on_score', 'liquidity_stress', 'inflation_expectation', 'pulse_level', 'as_of_utc'],
      schemaDesc: '基于跨资产相关性与流动性压力的综合宏观脉搏判定',
    },

    // 4. System Telemetry & Discovery Domain
    {
      id: 'system-grounding',
      domain: 'system',
      name: '全息架构地平线快照 (Grounding)',
      endpoint: '/api/v1/meta/grounding/snapshot',
      method: 'GET',
      sla: '< 5 ms',
      consumer: '前端全息透视 / 系统可用性公证',
      fields: ['topology', 'database_status', 'workers', 'active_routes', 'generated_at_utc'],
      schemaDesc: '服务端 600s 缓存的全景架构运行态快照与路由元数据',
    },
    {
      id: 'system-telemetry',
      domain: 'system',
      name: '后台 Worker 任务遥测与流水线心跳',
      endpoint: '/api/v1/meta/workers/telemetry',
      method: 'GET',
      sla: '< 8 ms',
      consumer: 'SRE 哨兵监控 / 自动报警',
      fields: ['workers', 'event_loop_lag_ms', 'queue_depth', 'error_count', 'last_pulse_utc'],
      schemaDesc: '各后台采集 Worker 运行周期、积压深度与事件循环延迟遥测',
    },
    {
      id: 'system-capabilities',
      domain: 'system',
      name: '前向微服务能力发现网格 (SSOT)',
      endpoint: '/api/v1/meta/capabilities',
      method: 'GET',
      sla: '< 5 ms',
      consumer: 'AI Agent / 下游微服务动态协商',
      fields: ['service', 'version', 'trading_day', 'market_session', 'invariants', 'domains', 'mailbox'],
      schemaDesc: '全站 11 大能力域与不变量元数据动态宣告',
    },
    {
      id: 'system-handbook',
      domain: 'system',
      name: 'AI 智能体开发接入手册',
      endpoint: '/api/v1/meta/agent-handbook',
      method: 'GET',
      sla: '< 5 ms',
      consumer: '开发人员 / LLM 接入辅助',
      fields: ['plain_text_handbook', 'transport_specs', 'tenant_matrix', 'code_examples'],
      schemaDesc: '面向 AI Agent 与工程团队的权威对接标准文档',
    },
    {
      id: 'system-llms',
      domain: 'system',
      name: 'LLM 标准上下文发现入口',
      endpoint: '/api/v1/meta/llms.txt',
      method: 'GET',
      sla: '< 5 ms',
      consumer: 'LLM / Web Crawlers / AI 客户端',
      fields: ['plain_text_llms_txt'],
      schemaDesc: '标准的 /llms.txt 格式服务能力与文档索引',
    },
  ];

  // Default provider mapping fallback
  const providers = statusData?.active_providers || {
    BTCUSDT: 'BINANCE',
    ETHUSDT: 'BINANCE',
    SOLUSDT: 'BINANCE',
    BNBUSDT: 'BINANCE',
    OKBUSDT: 'OKX',
    BGBUSDT: 'BITGET',
    HYPEUSDT: 'HYPERLIQUID',
  };

  const freshness = statusData?.telemetry_freshness || {};

  return (
    <div className="view-panel active integrations-hub-container" id="view-integrations-audit" role="tabpanel">
      {/* Top Banner & Header */}
      <div className="view-header" style={{ paddingBottom: '4px', marginBottom: '4px' }}>
        <div className="view-title-group bilingual-view-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Plug size={15} color="var(--accent-cyan)" />
            <h2 className="view-main-title bilingual-header-title-zh" style={{ margin: 0, fontSize: '16px' }}>
              {SYSTEM_BILINGUAL.gateway.zh}
            </h2>
            <span className="badge badge-cyan" style={{ fontSize: '9px', padding: '1px 5px' }}>STAGE 085</span>
            <span className="badge badge-bullish" style={{ fontSize: '9px', padding: '1px 5px' }}>ASOP v1.9.6</span>
          </div>
          <span className="bilingual-header-subtitle-en">{SYSTEM_BILINGUAL.gateway.en}</span>
          <p className="view-subtitle" style={{ margin: '1px 0 0 0' }}>
            面向量化系统 (TSO / Doctor-A / External Alpha) 的权威接入手册与实盘数据服务公证审核台
          </p>
        </div>

        {/* Live Node Heartbeat */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div
            style={{
              textAlign: 'right',
              fontSize: '11px',
              color: 'var(--text-muted)',
              lineHeight: 1.3,
            }}
          >
            <div>
              节点状态:{' '}
              <strong style={{ color: statusData?.is_running ? 'var(--color-emerald)' : 'var(--color-rose)' }}>
                {statusData?.is_running ? '● RUNNING' : '○ OFFLINE'}
              </strong>
            </div>
            <div>
              时钟: <span className="mono">{lastRefreshed}</span>
            </div>
          </div>
          <button
            onClick={fetchStatus}
            disabled={loading}
            className="btn-copy-grounding"
            style={{ padding: '4px 10px', fontSize: '11px' }}
            title="即时同步节点状态"
          >
            <RotateCcw size={11} className={loading ? 'spin' : ''} />
            <span>{loading ? '同步中...' : '即时刷新'}</span>
          </button>
        </div>
      </div>

      {errorMsg && (
        <div
          style={{
            background: 'var(--alert-bearish-bg)',
            border: '1px solid var(--alert-bearish-border)',
            color: 'var(--color-bearish)',
            padding: '8px 14px',
            borderRadius: '6px',
            marginBottom: '10px',
            fontSize: '0.82rem',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <AlertTriangle size={14} />
          <span>{errorMsg}</span>
        </div>
      )}

      {/* Segmented Dual-Pillar Tab Switcher */}
      <div className="grounding-tabs-container" style={{ margin: '2px 0 8px 0' }}>
        <button
          className={`grounding-tab-btn bilingual-tab-btn ${activeTab === 'onboarding' ? 'active' : ''}`}
          onClick={() => handleTabChange('onboarding')}
        >
          <div className="bilingual-tab-title-zh">
            <BookOpen size={13} />
            <span>开发者接入手册</span>
          </div>
          <span className="bilingual-tab-subtitle-en">CONSUMER ONBOARDING</span>
        </button>
        <button
          className={`grounding-tab-btn bilingual-tab-btn ${activeTab === 'mailbox' ? 'active' : ''}`}
          onClick={() => handleTabChange('mailbox')}
        >
          <div className="bilingual-tab-title-zh">
            <Mail size={13} />
            <span>生态需求信箱与协同</span>
          </div>
          <span className="bilingual-tab-subtitle-en">ECOSYSTEM MAILBOX & PROMPTS</span>
        </button>
        <button
          className={`grounding-tab-btn bilingual-tab-btn ${activeTab === 'audit' ? 'active' : ''}`}
          onClick={() => handleTabChange('audit')}
        >
          <div className="bilingual-tab-title-zh">
            <ShieldCheck size={13} />
            <span>数据服务权威审核台</span>
          </div>
          <span className="bilingual-tab-subtitle-en">DATA SERVICE AUDIT</span>
        </button>
        <button
          className={`grounding-tab-btn bilingual-tab-btn ${activeTab === 'grounding' ? 'active' : ''}`}
          onClick={() => handleTabChange('grounding')}
        >
          <div className="bilingual-tab-title-zh">
            <Compass size={13} />
            <span>项目接地与 API Mesh</span>
          </div>
          <span className="bilingual-tab-subtitle-en">PROJECT GROUNDING HUB</span>
        </button>
        <button
          className={`grounding-tab-btn bilingual-tab-btn ${activeTab === 'capabilities' ? 'active' : ''}`}
          onClick={() => handleTabChange('capabilities')}
        >
          <div className="bilingual-tab-title-zh">
            <Network size={13} />
            <span>全域业务能力树</span>
          </div>
          <span className="bilingual-tab-subtitle-en">CAPABILITY MAP & TREE</span>
        </button>
      </div>

      {/* ========================================================================= */}
      {/* PILLAR A: DEVELOPER ONBOARDING MANUAL                                      */}
      {/* ========================================================================= */}
      {activeTab === 'onboarding' && (
        <div className="pillar-onboarding-view" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {/* Section 1: Quick Start & Tenant Auth Protocol */}
          <div
            style={{
              background: 'var(--bg-surface)',
              border: '1px solid var(--border-subtle)',
              borderRadius: '8px',
              padding: '12px 16px',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <div className="bilingual-card-title">
                <h2 style={{ fontSize: '1.02rem', margin: 0, fontWeight: 600, color: 'var(--text-primary)' }} className="bilingual-card-title-zh">
                  1. 快速起步与安全认证协议
                </h2>
                <span className="bilingual-card-subtitle-en">QUICK START & AUTHENTICATION PROTOCOL</span>
              </div>
              <span style={{ fontSize: '0.78rem', color: 'var(--accent-sage)', fontWeight: 600 }}>
                RFC 6750 Bearer + Nonce Replay Protected
              </span>
            </div>
            <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', margin: '0 0 10px 0' }}>
              Market-Hub 对外提供严格的<strong>只读多租户网关 (ReadOnlyTenantGatewayMiddleware)</strong>。
              所有下游消费方发起请求时，必须在 HTTP Header 中携带身份凭证与防重放时戳：
            </p>

            {/* Autonomous Agent One-Step Integration Prompt Card */}
            <div
              className="agent-prompt-card"
              style={{
                background: 'var(--bg-surface-sunken)',
                border: '1px solid var(--border-subtle)',
                borderLeft: '3px solid var(--accent-cyan)',
                borderRadius: '6px',
                padding: '10px 14px',
                marginBottom: '10px',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Terminal size={16} color="var(--accent-cyan)" />
                  <strong style={{ fontSize: '0.96rem', color: 'var(--text-primary)' }}>
                    下游智能体自主接入一键 Prompt (Autonomous Agent One-Step Integration Prompt)
                  </strong>
                  <span className="badge badge-cyan" style={{ fontSize: '10px' }}>
                    ONE-STEP AGENT DISPATCH
                  </span>
                </div>
                <button
                  onClick={() => handleCopy('agent-prompt', agentPrompt)}
                  className="btn-copy-grounding"
                >
                  {copiedKey === 'agent-prompt' ? <Check size={13} /> : <Copy size={13} />}
                  <span>{copiedKey === 'agent-prompt' ? '已复制 Agent 指令' : '复制 Agent 一步接入 Prompt'}</span>
                </button>
              </div>
              <p style={{ margin: 0, fontSize: '0.84rem', color: 'var(--text-muted)', lineHeight: '1.5' }}>
                直接将此 Prompt 发送给下游量化系统（TSO / Doctor-A 等）的代码生成 Agent（Cursor / Claude Code / Codex / Antigravity），Agent 将严格遵循纯 Decimal、5秒防重放、:8443 物理端点与 Fail-Closed 规则，<strong>一步全自动完成客户端模块编写与基差接口连通验证</strong>：
              </p>
              <div
                className="mono"
                style={{
                  background: 'var(--code-bg)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: '6px',
                  padding: '12px 14px',
                  fontSize: '0.8rem',
                  lineHeight: '1.55',
                  color: 'var(--text-primary)',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  maxHeight: '170px',
                  overflowY: 'auto',
                }}
              >
                {agentPrompt}
              </div>
            </div>

            {/* Network Topology Notice Box */}
            <div
              style={{
                background: 'var(--alert-bearish-bg)',
                borderLeft: '4px solid var(--color-rose)',
                padding: '14px 18px',
                borderRadius: '0 8px 8px 0',
                fontSize: '0.86rem',
                color: 'var(--text-primary)',
                marginBottom: '16px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--color-rose)', fontWeight: 700 }}>
                <AlertTriangle size={15} />
                <span>物理网络端点接入拓扑说明 (SSOT Boundary):</span>
              </div>
              <div style={{ marginTop: '6px', color: 'var(--text-muted)', lineHeight: '1.5' }}>
                <div>• <strong>外部 Direct API (后端专用):</strong> <code className="mono" style={{ color: 'var(--accent-cyan)' }}>https://market-hub.20991234.xyz:8443</code> （必须携带 Authorization 与全套 X-TSO-* Headers）</div>
                <div>• <strong>前端 Ingress-BFF (浏览器专用):</strong> <code className="mono" style={{ color: 'var(--color-amber)' }}>https://market-hub.20991234.xyz:443</code> （仅限 SPA 页面公开探针，若携带 Token 外部调用将被网关直接 <strong>HTTP 403 阻断</strong>）</div>
                <div>• <strong>内网容器直连 (Intranet):</strong> <code className="mono" style={{ color: 'var(--color-emerald)' }}>http://127.0.0.1:&lt;INTRANET_PORT&gt;</code> （同机服务或 Docker 内部子网直通）</div>
              </div>
            </div>

            {/* Protocol Headers Table */}
            <div style={{ overflowX: 'auto', marginBottom: '16px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ background: 'var(--bg-surface-sunken)', textAlign: 'left' }}>
                    <th style={{ padding: '10px 14px', borderBottom: '1px solid var(--border-subtle)' }}>Header 名称</th>
                    <th style={{ padding: '10px 14px', borderBottom: '1px solid var(--border-subtle)' }}>类型 / 示例</th>
                    <th style={{ padding: '10px 14px', borderBottom: '1px solid var(--border-subtle)' }}>必选</th>
                    <th style={{ padding: '10px 14px', borderBottom: '1px solid var(--border-subtle)' }}>机制与防护说明</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ padding: '10px 14px', borderBottom: '1px solid var(--border-subtle)' }}>
                      <code className="mono">Authorization</code>
                    </td>
                    <td style={{ padding: '10px 14px', borderBottom: '1px solid var(--border-subtle)' }}>
                      <code className="mono">Bearer &lt;TENANT_TOKEN&gt;</code>
                    </td>
                    <td style={{ padding: '10px 14px', borderBottom: '1px solid var(--border-subtle)', color: 'var(--color-danger)', fontWeight: 600 }}>
                      必选
                    </td>
                    <td style={{ padding: '10px 14px', borderBottom: '1px solid var(--border-subtle)', color: 'var(--text-muted)' }}>
                      租户专属访问令牌，匹配注册的 Scopes (如 <code>read:crypto:*</code>, <code>read:ashare:*</code>, <code>read:macro:*</code>)
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: '10px 14px', borderBottom: '1px solid var(--border-subtle)' }}>
                      <code className="mono">X-TSO-Tenant-ID</code>
                    </td>
                    <td style={{ padding: '10px 14px', borderBottom: '1px solid var(--border-subtle)' }}>
                      <code className="mono">TENANT_TSO_CRYPTO</code> / <code className="mono">TENANT_DOCTORA_EQUITY</code>
                    </td>
                    <td style={{ padding: '10px 14px', borderBottom: '1px solid var(--border-subtle)', color: 'var(--color-danger)', fontWeight: 600 }}>
                      必选
                    </td>
                    <td style={{ padding: '10px 14px', borderBottom: '1px solid var(--border-subtle)', color: 'var(--text-muted)' }}>
                      租户命名空间隔离标识，严格与 Bearer Token 绑定身份一致，杜绝跨租户冒用
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: '10px 14px', borderBottom: '1px solid var(--border-subtle)' }}>
                      <code className="mono">X-TSO-Timestamp-UTC</code>
                    </td>
                    <td style={{ padding: '10px 14px', borderBottom: '1px solid var(--border-subtle)' }}>
                      <code className="mono">2026-09-19T12:00:00Z</code>
                    </td>
                    <td style={{ padding: '10px 14px', borderBottom: '1px solid var(--border-subtle)', color: 'var(--color-danger)', fontWeight: 600 }}>
                      必选
                    </td>
                    <td style={{ padding: '10px 14px', borderBottom: '1px solid var(--border-subtle)', color: 'var(--text-muted)' }}>
                      ISO-8601 UTC 时间戳 (必须带 Z)，服务端防重放窗口强制限制时钟漂移在 <strong>±5.0 秒</strong> 以内
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: '10px 14px', borderBottom: '1px solid var(--border-subtle)' }}>
                      <code className="mono">X-TSO-Nonce</code>
                    </td>
                    <td style={{ padding: '10px 14px', borderBottom: '1px solid var(--border-subtle)' }}>
                      <code className="mono">d3b07384-d113-469b-6597-123456789abc</code>
                    </td>
                    <td style={{ padding: '10px 14px', borderBottom: '1px solid var(--border-subtle)', color: 'var(--color-danger)', fontWeight: 600 }}>
                      必选
                    </td>
                    <td style={{ padding: '10px 14px', borderBottom: '1px solid var(--border-subtle)', color: 'var(--text-muted)' }}>
                      单次请求唯一 UUIDv4，网关内存按租户隔离去重滑窗 5.0 秒，重复 Nonce 立即拒绝 (HTTP 401)
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Invariant Alert Box */}
            <div
              style={{
                background: 'var(--bg-surface-sunken)',
                borderLeft: '4px solid var(--accent-sage)',
                padding: '14px 18px',
                borderRadius: '0 8px 8px 0',
                fontSize: '0.84rem',
                color: 'var(--text-primary)',
              }}
            >
              <strong>⚡ 速率限制、精度保障与 Fail-Closed 熔断规则:</strong>
              <ul style={{ margin: '6px 0 0 16px', padding: 0, color: 'var(--text-muted)', lineHeight: '1.6' }}>
                <li><strong>令牌桶速率配额：</strong>生产主力租户 (TSO / Doctor-A) 配额为 <strong>100 QPS (突发容量 150)</strong>；观察者租户为 20 QPS (突发 30)。超出将立即触发 HTTP 429 <code>TENANT_RATE_LIMIT_EXCEEDED</code> 并附带 <code>Retry-After</code>。</li>
                <li><strong>金融级纯 Decimal 边界：</strong>所有价格、基差、年化利率及金额字段在客户端反序列化时强制解析为 <code>Decimal</code> (例如 Python <code>json.loads(text, parse_float=Decimal)</code>)，严禁使用二进制 <code>float</code>。</li>
                <li><strong>Fail-Closed 真实性原则：</strong>若上游源全量故障或时钟漂移超限，网关触发 HTTP 503 <code>MarketSourceExhaustedError</code>，下游必须停机熔断报警，严禁采用任何虚假合成数据欺骗上层风控。</li>
              </ul>
            </div>
          </div>

          {/* Section 2: Authoritative API Contracts */}
          <div
            style={{
              background: 'var(--bg-surface)',
              border: '1px solid var(--border-subtle)',
              borderRadius: '12px',
              padding: '24px',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
              <div>
                <div className="bilingual-card-title">
                  <h2 style={{ fontSize: '1.15rem', margin: 0, fontWeight: 600, color: 'var(--text-primary)' }} className="bilingual-card-title-zh">
                    2. 核心权威数据契约全景
                  </h2>
                  <span className="bilingual-card-subtitle-en">AUTHORITATIVE API CONTRACTS</span>
                </div>
                <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                  4 大资产与治理域 · {contracts.length} 条权威路由契约 · 纯只读与受控写保证
                </span>
              </div>

              {/* Domain Filter Buttons */}
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {[
                  { id: 'all', label: `全部契约 (${contracts.length})` },
                  { id: 'crypto', label: `加密量化 (${contracts.filter((c) => c.domain === 'crypto').length})` },
                  { id: 'ashare', label: `A 股投研 (${contracts.filter((c) => c.domain === 'ashare').length})` },
                  { id: 'macro', label: `宏观走廊 (${contracts.filter((c) => c.domain === 'macro').length})` },
                  { id: 'system', label: `系统治理 (${contracts.filter((c) => c.domain === 'system').length})` },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setContractDomainFilter(tab.id as any)}
                    style={{
                      padding: '4px 10px',
                      fontSize: '0.78rem',
                      borderRadius: '6px',
                      border: '1px solid var(--border-subtle)',
                      cursor: 'pointer',
                      background: contractDomainFilter === tab.id ? 'var(--accent-sage)' : 'var(--bg-surface)',
                      color: contractDomainFilter === tab.id ? 'var(--text-inverse)' : 'var(--text-secondary)',
                      fontWeight: 600,
                    }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '16px' }}>
              {contracts
                .filter((c) => contractDomainFilter === 'all' || c.domain === contractDomainFilter)
                .map((c) => (
                  <div
                    key={c.id}
                    style={{
                      background: 'var(--bg-surface-sunken)',
                      border: '1px solid var(--border-subtle)',
                      borderRadius: '8px',
                      padding: '16px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                    }}
                  >
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <span
                          className="badge badge-cyan"
                          style={{
                            fontSize: '0.72rem',
                            fontWeight: 700,
                            padding: '2px 6px',
                            borderRadius: '4px',
                          }}
                        >
                          {c.method}
                        </span>
                        <span style={{ fontSize: '0.75rem', color: 'var(--accent-sage)', fontWeight: 600 }}>
                          SLA: {c.sla}
                        </span>
                      </div>
                      <div style={{ fontWeight: 600, fontSize: '0.95rem', color: 'var(--text-primary)', marginBottom: '4px' }}>
                        {c.name}
                      </div>
                      <div
                        className="mono"
                        style={{
                          fontSize: '0.8rem',
                          color: 'var(--text-muted)',
                          background: 'var(--bg-surface)',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          marginBottom: '8px',
                          wordBreak: 'break-all',
                        }}
                      >
                        {c.endpoint}
                      </div>
                      <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', margin: '0 0 10px 0' }}>
                        {c.schemaDesc}
                      </p>
                    </div>

                    <div>
                      <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)', marginBottom: '6px' }}>
                        下游消费方: <strong>{c.consumer}</strong>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {c.fields.slice(0, 5).map((f) => (
                          <span
                            key={f}
                            className="mono"
                            style={{
                              fontSize: '0.7rem',
                              background: 'var(--bg-surface)',
                              padding: '2px 5px',
                              borderRadius: '3px',
                              border: '1px solid var(--border-subtle)',
                            }}
                          >
                            {f}
                          </span>
                        ))}
                        {c.fields.length > 5 && (
                          <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', alignSelf: 'center' }}>
                            +{c.fields.length - 5}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>

          {/* Section 3: Polyglot SDK Snippets */}
          <div
            style={{
              background: 'var(--bg-surface)',
              border: '1px solid var(--border-subtle)',
              borderRadius: '12px',
              padding: '24px',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div className="bilingual-card-title">
                <h2 style={{ fontSize: '1.15rem', margin: 0, fontWeight: 600, color: 'var(--text-primary)' }} className="bilingual-card-title-zh">
                  3. 多语言一键集成代码
                </h2>
                <span className="bilingual-card-subtitle-en">POLYGLOT CLIENT SDK SNIPPETS</span>
              </div>
              {/* Language Selector */}
              <div style={{ display: 'flex', gap: '6px' }}>
                <button
                  onClick={() => setCodeLang('python')}
                  style={{
                    padding: '4px 12px',
                    fontSize: '0.8rem',
                    borderRadius: '6px',
                    border: '1px solid var(--border-subtle)',
                    cursor: 'pointer',
                    background: codeLang === 'python' ? 'var(--accent-sage)' : 'var(--bg-surface)',
                    color: codeLang === 'python' ? 'var(--text-inverse)' : 'var(--text-secondary)',
                    fontWeight: 600,
                  }}
                >
                  Python (纯 Decimal)
                </button>
                <button
                  onClick={() => setCodeLang('ts')}
                  style={{
                    padding: '4px 12px',
                    fontSize: '0.8rem',
                    borderRadius: '6px',
                    border: '1px solid var(--border-subtle)',
                    cursor: 'pointer',
                    background: codeLang === 'ts' ? 'var(--accent-sage)' : 'var(--bg-surface)',
                    color: codeLang === 'ts' ? 'var(--text-inverse)' : 'var(--text-secondary)',
                    fontWeight: 600,
                  }}
                >
                  TypeScript
                </button>
                <button
                  onClick={() => setCodeLang('curl')}
                  style={{
                    padding: '4px 12px',
                    fontSize: '0.8rem',
                    borderRadius: '6px',
                    border: '1px solid var(--border-subtle)',
                    cursor: 'pointer',
                    background: codeLang === 'curl' ? 'var(--accent-sage)' : 'var(--bg-surface)',
                    color: codeLang === 'curl' ? 'var(--text-inverse)' : 'var(--text-secondary)',
                    fontWeight: 600,
                  }}
                >
                  cURL / Shell
                </button>
              </div>
            </div>

            <div style={{ position: 'relative' }}>
              <button
                onClick={() =>
                  handleCopy(
                    'sdk-code',
                    codeLang === 'python' ? pythonSnippet : codeLang === 'ts' ? tsSnippet : curlSnippet
                  )
                }
                style={{
                  position: 'absolute',
                  top: '12px',
                  right: '12px',
                  padding: '6px 12px',
                  fontSize: '0.78rem',
                  background: 'var(--bg-surface)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  color: copiedKey === 'sdk-code' ? 'var(--color-success)' : 'var(--text-primary)',
                  fontWeight: 600,
                  zIndex: 2,
                }}
              >
                {copiedKey === 'sdk-code' ? '✓ 已复制到剪贴板' : '📋 复制完整代码'}
              </button>
              <pre
                className="mono"
                style={{
                  background: 'var(--bg-surface-sunken)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: '8px',
                  padding: '16px',
                  fontSize: '0.82rem',
                  lineHeight: '1.5',
                  overflowX: 'auto',
                  margin: 0,
                  color: 'var(--text-primary)',
                }}
              >
                {codeLang === 'python' && pythonSnippet}
                {codeLang === 'ts' && tsSnippet}
                {codeLang === 'curl' && curlSnippet}
              </pre>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* PILLAR M: QUANT ECOSYSTEM MAILBOX & AGENT PROMPTS                          */}
      {/* ========================================================================= */}
      {activeTab === 'mailbox' && (
        <div className="pillar-mailbox-view" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {/* Header Description */}
          <div className="integrations-hero-card" style={{ padding: '10px 14px' }}>
            <div style={{ maxWidth: '820px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <Mail size={16} color="var(--accent-cyan)" />
                <div className="bilingual-card-title">
                  <h3 style={{ fontSize: '1.02rem', margin: 0, fontWeight: 700, color: 'var(--text-primary)' }} className="bilingual-card-title-zh">
                    量化生态信箱与跨项目智能体协同门户
                  </h3>
                  <span className="bilingual-card-subtitle-en" style={{ fontSize: '7.5px' }}>ECOSYSTEM MAILBOX & PROMPTS</span>
                </div>
              </div>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: 0, lineHeight: 1.4 }}>
                Market-Hub 作为 7x24 持续运行的权威数据中枢，向下游终端项目 (Doctor-A, TSO) 提供公共能力动态感知 (Forward Mesh) 与异步需求工单信箱 (Reverse Mailbox)。
              </p>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <a
                href="/api/v1/meta/capabilities"
                target="_blank"
                rel="noreferrer"
                className="btn-copy-grounding"
                style={{ textDecoration: 'none', background: 'var(--bg-surface-hover)', padding: '4px 10px', fontSize: '11px' }}
              >
                <ExternalLink size={12} />
                <span>查看 JSON SSOT</span>
              </a>
              <a
                href="/api/v1/meta/agent-handbook"
                target="_blank"
                rel="noreferrer"
                className="btn-copy-grounding"
                style={{ textDecoration: 'none', background: 'var(--bg-surface-hover)', padding: '4px 10px', fontSize: '11px' }}
              >
                <FileCode size={12} />
                <span>查看 AI 手册</span>
              </a>
            </div>
          </div>

          {/* Sub-panel A: 1-Click Cross-Project Agent Prompts */}
          <div className="integrations-panel">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
              <div>
                <div className="bilingual-card-title">
                  <h3 style={{ fontSize: '1.05rem', margin: 0, fontWeight: 700, color: 'var(--text-primary)' }} className="bilingual-card-title-zh">
                    1. 跨项目智能体协同指令集
                  </h3>
                  <span className="bilingual-card-subtitle-en">1-CLICK CROSS-PROJECT AGENT PROMPTS</span>
                </div>
                <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', margin: '4px 0 0 0' }}>
                  在下游项目的 AI Agent (如 Cursor / Claude / VS Code Codex) 中直接粘贴对应指令，即可一键唤醒与 Market-Hub 的双向生态信箱协同。
                </p>
              </div>
              <span className="badge badge-bullish">
                STAGE 085 / ASOP v1.9.6 COMPLIANT
              </span>
            </div>

            <div className="integrations-prompt-grid">
              {/* Doctor-A Prompt Card */}
              <div className="integrations-prompt-card">
                <div className="integrations-prompt-header">
                  <div className="integrations-prompt-title">
                    <Activity size={16} color="var(--accent-cyan)" />
                    <span>Doctor-A 选股与风控智能体</span>
                  </div>
                  <button
                    onClick={() => handleCopy('prompt-doctora', doctoraAgentPrompt)}
                    className="btn-copy-grounding"
                  >
                    {copiedKey === 'prompt-doctora' ? <Check size={12} /> : <Copy size={12} />}
                    <span>{copiedKey === 'prompt-doctora' ? '已复制指令' : '复制 Prompt'}</span>
                  </button>
                </div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', lineHeight: 1.4 }}>
                  用于 Doctor-A 智能体自动发现 A 股因子、提交新指标需求工单，并在迭代时读取处理回信。
                </div>
                <pre className="integrations-code-box">
                  {doctoraAgentPrompt}
                </pre>
              </div>

              {/* TSO Prompt Card */}
              <div className="integrations-prompt-card">
                <div className="integrations-prompt-header">
                  <div className="integrations-prompt-title">
                    <Zap size={16} color="var(--color-amber)" />
                    <span>TSO 加密多周期策略智能体</span>
                  </div>
                  <button
                    onClick={() => handleCopy('prompt-tso', tsoAgentPrompt)}
                    className="btn-copy-grounding"
                  >
                    {copiedKey === 'prompt-tso' ? <Check size={12} /> : <Copy size={12} />}
                    <span>{copiedKey === 'prompt-tso' ? '已复制指令' : '复制 Prompt'}</span>
                  </button>
                </div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', lineHeight: 1.4 }}>
                  用于 TSO 加密量化智能体消费基差时钟与指标矩阵，提出新时序或聚合需求工单。
                </div>
                <pre className="integrations-code-box">
                  {tsoAgentPrompt}
                </pre>
              </div>

              {/* Market-Hub Architecture Lead Review Card */}
              <div className="integrations-prompt-card">
                <div className="integrations-prompt-header">
                  <div className="integrations-prompt-title">
                    <ShieldCheck size={16} color="var(--color-emerald)" />
                    <span>Market-Hub 架构评审与回信中枢</span>
                  </div>
                  <button
                    onClick={() => handleCopy('prompt-markethub', markethubReviewPrompt)}
                    className="btn-copy-grounding"
                  >
                    {copiedKey === 'prompt-markethub' ? <Check size={12} /> : <Copy size={12} />}
                    <span>{copiedKey === 'prompt-markethub' ? '已复制指令' : '复制 Prompt'}</span>
                  </button>
                </div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', lineHeight: 1.4 }}>
                  当切换到 Market-Hub 项目时，唤醒 Agent-B 检查下游收件箱、评估技术可行性并签发正式回信。
                </div>
                <pre className="integrations-code-box">
                  {markethubReviewPrompt}
                </pre>
              </div>
            </div>
          </div>

          {/* Sub-panel B: Dynamic Capability Manifest Overview */}
          <div className="integrations-panel">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <div>
                <div className="bilingual-card-title">
                  <h3 style={{ fontSize: '1.05rem', margin: 0, fontWeight: 700, color: 'var(--text-primary)' }} className="bilingual-card-title-zh">
                    2. 动态能力拓扑与架构不变式
                  </h3>
                  <span className="bilingual-card-subtitle-en">DYNAMIC CAPABILITY MESH SSOT</span>
                </div>
                <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', margin: '4px 0 0 0' }}>
                  从当前装载路由与契约动态聚合生成 (A042-05)，杜绝手工维护文档漂移。
                </p>
              </div>
              <button
                onClick={fetchCapabilities}
                className="btn-copy-grounding"
                style={{ padding: '6px 12px', fontSize: '11px' }}
              >
                <RotateCcw size={12} className={capLoading ? 'spin' : ''} />
                <span>{capLoading ? '正在刷新...' : '刷新能力'}</span>
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px', marginBottom: '8px' }}>
              <div style={{ background: 'var(--bg-surface-sunken)', padding: '14px', borderRadius: '8px', border: '1px solid var(--border-subtle)' }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>服务与版本</div>
                <div style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--text-primary)', marginTop: '4px' }}>
                  market-hub v{capabilitiesData?.version || '1.3.0'}
                </div>
              </div>
              <div style={{ background: 'var(--bg-surface-sunken)', padding: '14px', borderRadius: '8px', border: '1px solid var(--border-subtle)' }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>交易日与时段状态</div>
                <div style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--accent-cyan)', marginTop: '4px' }}>
                  {capabilitiesData?.trading_day || '--'} · {capabilitiesData?.market_session || 'OPEN'}
                </div>
              </div>
              <div style={{ background: 'var(--bg-surface-sunken)', padding: '14px', borderRadius: '8px', border: '1px solid var(--border-subtle)' }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>因果时延窗口 (W)</div>
                <div style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--color-emerald)', marginTop: '4px' }}>
                  {capabilitiesData?.invariants?.causal_lag_window_seconds || '3.0'}s (Pure Decimal)
                </div>
              </div>
              <div style={{ background: 'var(--bg-surface-sunken)', padding: '14px', borderRadius: '8px', border: '1px solid var(--border-subtle)' }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>只读与非交易物理锁</div>
                <div style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--color-amber)', marginTop: '4px' }}>
                  STRICT_READONLY_405
                </div>
              </div>
            </div>

            {/* Active Domains Badges */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {capabilitiesData?.domains ? (
                Object.entries(capabilitiesData.domains).map(([k, v]: [string, any]) => (
                  <div
                    key={k}
                    style={{
                      background: 'var(--bg-surface-sunken)',
                      border: '1px solid var(--border-subtle)',
                      borderRadius: '6px',
                      padding: '6px 12px',
                      fontSize: '0.8rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    <span style={{ color: 'var(--color-emerald)', fontSize: '0.65rem' }}>●</span>
                    <strong style={{ color: 'var(--text-primary)' }}>{k}</strong>
                    <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>({v.status})</span>
                  </div>
                ))
              ) : (
                <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>正在同步能力集...</span>
              )}
            </div>
          </div>

          {/* Sub-panel C: Interactive Ticket Status Tracker */}
          <div className="integrations-panel">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <div>
                <div className="bilingual-card-title">
                  <h3 style={{ fontSize: '1.05rem', margin: 0, fontWeight: 700, color: 'var(--text-primary)' }} className="bilingual-card-title-zh">
                    3. 异步工单状态实时查询
                  </h3>
                  <span className="bilingual-card-subtitle-en">DEMAND TICKET STATUS TRACKER</span>
                </div>
                <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', margin: '4px 0 0 0' }}>
                  无需鉴权公开查询工单流转状态 (A042-03)，敏感内部实现信息已脱敏过滤。
                </p>
              </div>
              <span className="badge badge-neutral" style={{ fontSize: '10px' }}>
                PUBLIC STATUS ENDPOINT
              </span>
            </div>

            {/* Search Input Bar */}
            <form onSubmit={handleLookupTicket} style={{ display: 'flex', gap: '10px', marginBottom: '14px' }}>
              <input
                type="text"
                value={ticketQueryId}
                onChange={(e) => setTicketQueryId(e.target.value)}
                placeholder="输入工单编号，例如: REQ-20260921-DOCTORA-E6A1B2"
                style={{
                  flex: 1,
                  padding: '9px 14px',
                  borderRadius: '6px',
                  border: '1px solid var(--border-subtle)',
                  background: 'var(--bg-surface-sunken)',
                  color: 'var(--text-primary)',
                  fontSize: '0.85rem',
                  fontFamily: 'var(--font-mono, monospace)',
                }}
              />
              <button
                type="submit"
                disabled={ticketLoading}
                className="btn btn-primary"
                style={{
                  padding: '9px 18px',
                  borderRadius: '6px',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <Search size={14} />
                <span>{ticketLoading ? '查询中...' : '查询工单状态'}</span>
              </button>
            </form>

            {ticketError && (
              <div
                style={{
                  background: 'var(--alert-bearish-bg)',
                  border: '1px solid var(--alert-bearish-border)',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  color: 'var(--color-bearish)',
                  fontSize: '0.85rem',
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <AlertTriangle size={15} />
                <span>{ticketError}</span>
              </div>
            )}

            {ticketResult && (
              <div
                style={{
                  background: 'var(--bg-surface-sunken)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: '10px',
                  padding: '20px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '14px',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span className="mono" style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                      {ticketResult.ticket_id}
                    </span>
                    <span
                      className={`badge ${
                        ticketResult.status === 'ACCEPTED_SCHEDULED'
                          ? 'badge-bullish'
                          : ticketResult.status === 'UNDER_EVALUATION'
                          ? 'badge-warning'
                          : ticketResult.status === 'REJECTED'
                          ? 'badge-bearish'
                          : 'badge-cyan'
                      }`}
                      style={{
                        padding: '3px 10px',
                        fontSize: '0.78rem',
                        fontWeight: 700,
                      }}
                    >
                      {ticketResult.status}
                    </span>
                  </div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                    发起租户: <strong style={{ color: 'var(--text-primary)' }}>{ticketResult.sender_tenant}</strong> · {ticketResult.created_at_utc}
                  </div>
                </div>

                <div style={{ fontSize: '0.92rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                  【{ticketResult.category}】{ticketResult.title}
                </div>

                {/* Reply section if present */}
                {ticketResult.reply && (
                  <div
                    style={{
                      background: 'var(--badge-bullish-bg)',
                      border: '1px solid var(--badge-bullish-border)',
                      borderRadius: '8px',
                      padding: '14px 18px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--color-emerald)', fontWeight: 700, fontSize: '0.9rem' }}>
                        <ShieldCheck size={16} />
                        <span>架构组正式回信 ({ticketResult.reply.decision})</span>
                      </div>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                        {ticketResult.reply.replied_by} · {ticketResult.reply.replied_at_utc}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.85rem', color: 'var(--text-primary)' }}>
                      {ticketResult.reply.reply_note}
                    </div>
                    {ticketResult.reply.scheduled_stage && (
                      <div style={{ fontSize: '0.8rem', color: 'var(--accent-cyan)' }}>
                        计划排期阶段: <strong>{ticketResult.reply.scheduled_stage}</strong>
                        {ticketResult.reply.target_endpoint && ` · 目标接口: ${ticketResult.reply.target_endpoint}`}
                      </div>
                    )}
                  </div>
                )}

                {/* History timeline */}
                {ticketResult.history && ticketResult.history.length > 0 && (
                  <div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '6px' }}>流转审计链:</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {ticketResult.history.map((h: any, idx: number) => (
                        <div key={idx} style={{ fontSize: '0.78rem', color: 'var(--text-muted)', display: 'flex', gap: '8px' }}>
                          <span className="mono">{h.time_utc}</span>
                          <strong style={{ color: 'var(--text-primary)' }}>[{h.action}]</strong>
                          <span>{h.actor}: {h.note || ''}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* PILLAR B: DATA SERVICE AUDIT CONSOLE                                      */}
      {/* ========================================================================= */}
      {activeTab === 'audit' && (
        <div className="pillar-audit-view" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {/* Section 1: Financial-Grade Invariant Verification */}
          <div className="integrations-panel" style={{ padding: '8px 12px', gap: '6px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div className="bilingual-card-title">
                <h3 style={{ fontSize: '0.92rem', margin: 0, fontWeight: 700, color: 'var(--text-primary)' }} className="bilingual-card-title-zh">
                  1. 金融级工程不变式实盘公证
                </h3>
                <span className="bilingual-card-subtitle-en" style={{ fontSize: '7.5px' }}>FINANCIAL-GRADE INVARIANTS AUDIT</span>
              </div>
              <span className="badge badge-bullish" style={{ fontSize: '9px', padding: '2px 6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <ShieldCheck size={11} /> 3/3 绝对防线公证通过
              </span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '8px' }}>
              {/* Badge 1 */}
              <div
                style={{
                  background: 'var(--bg-surface-sunken)',
                  border: '1px solid var(--border-subtle)',
                  borderLeft: '3px solid var(--color-emerald)',
                  borderRadius: '6px',
                  padding: '6px 10px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '3px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <ShieldCheck size={13} color="var(--color-emerald)" />
                    <span style={{ fontWeight: 600, fontSize: '0.84rem', color: 'var(--text-primary)' }}>
                      Zero Synthetic Mock
                    </span>
                  </div>
                  <span style={{ fontSize: '0.65rem', color: 'var(--color-emerald)', fontWeight: 700, fontFamily: 'var(--font-mono)' }}>
                    GATE-TRUTHFUL-STATE-MACHINE
                  </span>
                </div>
                <p style={{ fontSize: '0.74rem', color: 'var(--text-muted)', margin: 0, lineHeight: 1.35 }}>
                  对外交付 API 100% 绑定真实交易所现货与永续订单流。离线或异常时严格呈现 5 态机，禁止任何静态假价格（如 76208.59）或假健康度。
                </p>
              </div>

              {/* Badge 2 */}
              <div
                style={{
                  background: 'var(--bg-surface-sunken)',
                  border: '1px solid var(--border-subtle)',
                  borderLeft: '3px solid var(--color-emerald)',
                  borderRadius: '6px',
                  padding: '6px 10px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '3px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <ShieldCheck size={13} color="var(--color-emerald)" />
                    <span style={{ fontWeight: 600, fontSize: '0.84rem', color: 'var(--text-primary)' }}>
                      100% Pure Decimal Domain
                    </span>
                  </div>
                  <span style={{ fontSize: '0.65rem', color: 'var(--color-emerald)', fontWeight: 700, fontFamily: 'var(--font-mono)' }}>
                    GATE-SCOPED-DECIMAL-INVARIANTS
                  </span>
                </div>
                <p style={{ fontSize: '0.74rem', color: 'var(--text-muted)', margin: 0, lineHeight: 1.35 }}>
                  核心金融域（价格、基差、K 线高开低收量）静态 AST 审计 0 浮点节点。全链路无 <code>float()</code> 二进制舍入误差，保证资金结算级精确。
                </p>
              </div>

              {/* Badge 3 */}
              <div
                style={{
                  background: 'var(--bg-surface-sunken)',
                  border: '1px solid var(--border-subtle)',
                  borderLeft: '3px solid var(--color-emerald)',
                  borderRadius: '6px',
                  padding: '6px 10px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '3px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <ShieldCheck size={13} color="var(--color-emerald)" />
                    <span style={{ fontWeight: 600, fontSize: '0.84rem', color: 'var(--text-primary)' }}>
                      因果时延守卫 (W ≥ 3s)
                    </span>
                  </div>
                  <span style={{ fontSize: '0.65rem', color: 'var(--color-emerald)', fontWeight: 700, fontFamily: 'var(--font-mono)' }}>
                    GATE-CAUSAL-LAG-TESTING
                  </span>
                </div>
                <p style={{ fontSize: '0.74rem', color: 'var(--text-muted)', margin: 0, lineHeight: 1.35 }}>
                  高频分时与多周期合成 K 线入库时间严格满足 <code>ingested_at_utc &gt;= close_time_utc + 3s</code>，坚决杜绝未闭线未来函数流入量化回测模型。
                </p>
              </div>
            </div>
          </div>

          {/* Section 2: Core-7 Data Provenance & Routing Matrix */}
          <div className="integrations-panel" style={{ padding: '8px 12px', gap: '6px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
              <div className="bilingual-card-title">
                <h3 style={{ fontSize: '0.92rem', margin: 0, fontWeight: 700, color: 'var(--text-primary)' }} className="bilingual-card-title-zh">
                  2. Core-7 标的数据源物理拓扑与实时溯源
                </h3>
                <span className="bilingual-card-subtitle-en" style={{ fontSize: '7.5px' }}>DATA PROVENANCE & ROUTING MATRIX</span>
              </div>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                动态绑定自生产节点 <code>/api/v1/market/status</code>
              </span>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                <thead>
                  <tr style={{ background: 'var(--bg-surface-sunken)', textAlign: 'left' }}>
                    <th style={{ padding: '6px 10px', borderBottom: '1px solid var(--border-subtle)' }}>标的代码</th>
                    <th style={{ padding: '6px 10px', borderBottom: '1px solid var(--border-subtle)' }}>现货提供源</th>
                    <th style={{ padding: '6px 10px', borderBottom: '1px solid var(--border-subtle)' }}>永续合约源</th>
                    <th style={{ padding: '6px 10px', borderBottom: '1px solid var(--border-subtle)' }}>连接协议</th>
                    <th style={{ padding: '6px 10px', borderBottom: '1px solid var(--border-subtle)' }}>数据落地引擎</th>
                    <th style={{ padding: '6px 10px', borderBottom: '1px solid var(--border-subtle)' }}>实时健康度</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(providers).map(([sym, prov]) => (
                    <tr key={sym}>
                      <td style={{ padding: '5px 10px', borderBottom: '1px solid var(--border-subtle)', fontWeight: 600 }}>
                        <span className="mono">{sym}</span>
                      </td>
                      <td style={{ padding: '5px 10px', borderBottom: '1px solid var(--border-subtle)' }}>
                        <span
                          className="mono"
                          style={{
                            background: 'var(--bg-surface-sunken)',
                            padding: '1px 5px',
                            borderRadius: '4px',
                            border: '1px solid var(--border-subtle)',
                            fontSize: '0.78rem',
                          }}
                        >
                          {prov}
                        </span>
                      </td>
                      <td style={{ padding: '5px 10px', borderBottom: '1px solid var(--border-subtle)' }}>
                        <span
                          className="mono"
                          style={{
                            background: 'var(--bg-surface-sunken)',
                            padding: '1px 5px',
                            borderRadius: '4px',
                            border: '1px solid var(--border-subtle)',
                            fontSize: '0.78rem',
                          }}
                        >
                          {prov === 'OKX' ? 'OKX PERP' : prov === 'BITGET' ? 'BITGET PERP' : prov === 'HYPERLIQUID' ? 'HYPERLIQUID L1' : 'BINANCE FUTURES'}
                        </span>
                      </td>
                      <td style={{ padding: '5px 10px', borderBottom: '1px solid var(--border-subtle)', color: 'var(--text-muted)' }}>
                        REST Polling + Ingestion Worker
                      </td>
                      <td style={{ padding: '5px 10px', borderBottom: '1px solid var(--border-subtle)' }}>
                        <span style={{ color: 'var(--accent-sage)', fontWeight: 600 }}>TimescaleDB 2.14.2</span>
                      </td>
                      <td style={{ padding: '5px 10px', borderBottom: '1px solid var(--border-subtle)', color: 'var(--color-emerald)', fontWeight: 600 }}>
                        ● HEALTHY
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Section 3: 4-Tier Physical Freshness Waterfall */}
          <div className="integrations-panel" style={{ padding: '8px 12px', gap: '6px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div className="bilingual-card-title">
                <h3 style={{ fontSize: '0.92rem', margin: 0, fontWeight: 700, color: 'var(--text-primary)' }} className="bilingual-card-title-zh">
                  3. 物理时效性四级流水线瀑布流
                </h3>
                <span className="bilingual-card-subtitle-en" style={{ fontSize: '7.5px' }}>TRUTHFUL FRESHNESS WATERFALL</span>
              </div>
              <span className="badge badge-cyan" style={{ fontSize: '9px', padding: '1px 5px' }}>
                4-STAGE ISO-8601 UTC
              </span>
            </div>
            <p style={{ fontSize: '0.76rem', color: 'var(--text-muted)', margin: '0 0 6px 0' }}>
              真实呈现数据从交易所线路上云、衍生计算、价格轮询到调度落地的完整时间戳时序：
            </p>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: '8px',
                position: 'relative',
              }}
            >
              {/* Stage 1 */}
              <div
                style={{
                  background: 'var(--bg-surface-sunken)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: '6px',
                  padding: '8px 10px',
                }}
              >
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '2px' }}>
                  STAGE 1: 交易所原始入库
                </div>
                <div style={{ fontWeight: 600, fontSize: '0.82rem', color: 'var(--text-primary)', marginBottom: '4px' }}>
                  last_ingestion_utc
                </div>
                <div className="mono" style={{ fontSize: '0.76rem', color: 'var(--accent-sage)' }}>
                  {freshness.last_ingestion_utc || statusData?.last_cycle_utc || '2026-09-17T06:05:14Z'}
                </div>
              </div>

              {/* Stage 2 */}
              <div
                style={{
                  background: 'var(--bg-surface-sunken)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: '6px',
                  padding: '8px 10px',
                }}
              >
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '2px' }}>
                  STAGE 2: 衍生切片计算
                </div>
                <div style={{ fontWeight: 600, fontSize: '0.82rem', color: 'var(--text-primary)', marginBottom: '4px' }}>
                  last_derived_snapshot_utc
                </div>
                <div className="mono" style={{ fontSize: '0.76rem', color: 'var(--accent-sage)' }}>
                  {freshness.last_derived_snapshot_utc || statusData?.last_cycle_utc || '2026-09-17T06:05:14Z'}
                </div>
              </div>

              {/* Stage 3 */}
              <div
                style={{
                  background: 'var(--bg-surface-sunken)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: '6px',
                  padding: '8px 10px',
                }}
              >
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '2px' }}>
                  STAGE 3: 标的价格实时探针
                </div>
                <div style={{ fontWeight: 600, fontSize: '0.82rem', color: 'var(--text-primary)', marginBottom: '4px' }}>
                  last_price_probe_utc
                </div>
                <div className="mono" style={{ fontSize: '0.76rem', color: 'var(--accent-sage)' }}>
                  {freshness.last_price_probe_utc || statusData?.last_cycle_utc || '2026-09-17T06:05:14Z'}
                </div>
              </div>

              {/* Stage 4 */}
              <div
                style={{
                  background: 'var(--bg-surface-sunken)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: '6px',
                  padding: '8px 10px',
                }}
              >
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '2px' }}>
                  STAGE 4: 循环结算完成
                </div>
                <div style={{ fontWeight: 600, fontSize: '0.82rem', color: 'var(--text-primary)', marginBottom: '4px' }}>
                  last_completed_cycle_utc
                </div>
                <div className="mono" style={{ fontSize: '0.76rem', color: 'var(--accent-sage)' }}>
                  {freshness.last_completed_cycle_utc || statusData?.last_cycle_utc || '2026-09-17T06:05:14Z'}
                </div>
              </div>
            </div>

            <div
              style={{
                marginTop: '6px',
                display: 'flex',
                gap: '16px',
                fontSize: '0.78rem',
                color: 'var(--text-muted)',
                background: 'var(--bg-surface-sunken)',
                padding: '6px 12px',
                borderRadius: '6px',
              }}
            >
              <div>
                调度器唤醒抖动:{' '}
                <strong className="mono" style={{ color: 'var(--text-primary)' }}>
                  {freshness.event_loop_lag_ms !== undefined ? `${freshness.event_loop_lag_ms} ms` : '877 ms'}
                </strong>{' '}
                <span style={{ fontSize: '0.72rem' }}>(PM 豁免阈值 &lt; 3000ms)</span>
              </div>
              <div>
                Worker 排队积压:{' '}
                <strong className="mono" style={{ color: 'var(--color-emerald)' }}>
                  {freshness.worker_backlog_count || 0}
                </strong>
              </div>
              <div>
                周期合并计数:{' '}
                <strong className="mono" style={{ color: 'var(--text-primary)' }}>
                  {freshness.coalesced_cycle_count || 0}
                </strong>
              </div>
            </div>
          </div>

          {/* Section 4: Registered Tenants & Compliance Overview */}
          <div className="integrations-panel" style={{ padding: '8px 12px', gap: '6px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
              <div className="bilingual-card-title">
                <h3 style={{ fontSize: '0.92rem', margin: 0, fontWeight: 700, color: 'var(--text-primary)' }} className="bilingual-card-title-zh">
                  4. 注册租户与合规状态凭证
                </h3>
                <span className="bilingual-card-subtitle-en" style={{ fontSize: '7.5px' }}>TENANT COMPLIANCE AUDIT</span>
              </div>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                多租户隔离与速率配额
              </span>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                <thead>
                  <tr style={{ background: 'var(--bg-surface-sunken)', textAlign: 'left' }}>
                    <th style={{ padding: '6px 10px', borderBottom: '1px solid var(--border-subtle)' }}>租户 ID</th>
                    <th style={{ padding: '6px 10px', borderBottom: '1px solid var(--border-subtle)' }}>系统受众 Audience</th>
                    <th style={{ padding: '6px 10px', borderBottom: '1px solid var(--border-subtle)' }}>授权 Scopes</th>
                    <th style={{ padding: '6px 10px', borderBottom: '1px solid var(--border-subtle)' }}>速率限制</th>
                    <th style={{ padding: '6px 10px', borderBottom: '1px solid var(--border-subtle)' }}>合规状态</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ padding: '5px 10px', borderBottom: '1px solid var(--border-subtle)', fontWeight: 600 }}>
                      <code className="mono">TENANT_TSO_CRYPTO</code>
                    </td>
                    <td style={{ padding: '5px 10px', borderBottom: '1px solid var(--border-subtle)' }}>
                      <code className="mono">market-hub-v1</code>
                    </td>
                    <td style={{ padding: '5px 10px', borderBottom: '1px solid var(--border-subtle)' }}>
                      <span className="mono" style={{ fontSize: '0.76rem' }}>read:crypto:*, read:indicators, read:market:*, read:telemetry</span>
                    </td>
                    <td style={{ padding: '5px 10px', borderBottom: '1px solid var(--border-subtle)' }}>
                      100 QPS / 150 burst
                    </td>
                    <td style={{ padding: '5px 10px', borderBottom: '1px solid var(--border-subtle)', color: 'var(--color-emerald)', fontWeight: 600 }}>
                      ● ACTIVE / AUTHORIZED
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: '5px 10px', borderBottom: '1px solid var(--border-subtle)', fontWeight: 600 }}>
                      <code className="mono">TENANT_DOCTORA_EQUITY</code>
                    </td>
                    <td style={{ padding: '5px 10px', borderBottom: '1px solid var(--border-subtle)' }}>
                      <code className="mono">market-hub-v1</code>
                    </td>
                    <td style={{ padding: '5px 10px', borderBottom: '1px solid var(--border-subtle)' }}>
                      <span className="mono" style={{ fontSize: '0.76rem' }}>read:ashare:*, read:ashare:watchlist, read:macro:*, read:usequity:*, read:indicators, read:market:*, read:telemetry</span>
                    </td>
                    <td style={{ padding: '5px 10px', borderBottom: '1px solid var(--border-subtle)' }}>
                      100 QPS / 150 burst
                    </td>
                    <td style={{ padding: '5px 10px', borderBottom: '1px solid var(--border-subtle)', color: 'var(--color-emerald)', fontWeight: 600 }}>
                      ● ACTIVE / AUTHORIZED
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* PILLAR D: PROJECT GROUNDING & API MESH                                      */}
      {/* ========================================================================= */}
      {activeTab === 'grounding' && (
        <div className="pillar-grounding-view" style={{ marginTop: '8px' }}>
          <ProjectGroundingView />
        </div>
      )}

      {/* ========================================================================= */}
      {/* PILLAR E: SYSTEM CAPABILITY MAP & FUNCTION TREE                           */}
      {/* ========================================================================= */}
      {activeTab === 'capabilities' && (
        <div className="pillar-capabilities-view" style={{ marginTop: '8px' }}>
          <SystemCapabilityMapView />
        </div>
      )}
    </div>
  );
};

export default IntegrationsAuditHub;
